import React, { useEffect, useState, type Dispatch, type SetStateAction } from 'react';

import { openArtifactDisplay } from '../../context/ArtifactDisplayContext';
import type { Task } from '../../services/taskService';
import { planningTaskStateLabel } from '../../utils/planningTaskPresentation';
import { usePlanningTasks } from '../PlanningTasks';
import { TaskChecklistRows } from '../TaskChecklist';
import type { TaskChecklistItem } from '../TaskChecklist';

import {
  COMPLETION_GRACE_MS, dockTasks, followCompletion, graceTaskIds, isActivePlanningTask, settleTask,
} from './activeTasksDockModel';
import type { CompletionFollow } from './activeTasksDockModel';
import { useDeparture, useSheetHeight } from './activeTasksDockMotion';
import { NotifyMeButton } from './NotifyMeButton';

import '../ActivityRing.css';
import './ActiveTasksDock.css';

interface Props {
  projectId?: string | null;
  hidden?: boolean;
  /** The planning run is live: its queued and in-progress tasks are the ones to dock. */
  running?: boolean;
}

type SetFollow = Dispatch<SetStateAction<CompletionFollow>>;

/** One ten-second timer per task in grace; each settles its own task when it fires. */
const useGraceTimers = (follow: CompletionFollow, setFollow: SetFollow): void => {
  const graceKey = graceTaskIds(follow).join('\n');

  // Timers live inside the effect so that a re-run (including React's development
  // double invocation) always arms a fresh set and the cleanup always clears them.
  useEffect(() => {
    const timers = graceKey.split('\n').filter(Boolean).map((id) => globalThis.setTimeout(() => {
      setFollow((prev) => settleTask(prev, id));
    }, COMPLETION_GRACE_MS));

    return () => timers.forEach((timer) => globalThis.clearTimeout(timer));
  }, [graceKey, setFollow]);
};

/** Follow which tasks have been active so a finished one lingers, marked done, before it is hidden. */
const useCompletionFollow = (activeIds: string[]): CompletionFollow => {
  const [follow, setFollow] = useState<CompletionFollow>({});
  // Newly active or newly finished tasks are recorded during render, keyed by the task list.
  const next = followCompletion(follow, activeIds);

  if (next !== follow) setFollow(next);
  useGraceTimers(next, setFollow);

  return next;
};

const toItem = (task: Task, running = true): TaskChecklistItem => ({
  id: task.id, title: task.title, status: task.status, statusLabel: planningTaskStateLabel(task.status),
  statusTitle: task.execution_step, completionOnly: true, activityFrozen: !running,
});

const viewTask = (itemId: string): void => openArtifactDisplay({ tab: 'tasks', itemId, source: 'timeline' });

const summary = (shown: Task[], running: boolean): string => {
  if (!running) return shown.every((task) => task.status === 'done') ? 'All done' : 'Stopped';
  const inProgress = shown.filter((task) => task.status !== 'queued' && isActivePlanningTask(task)).length;
  const queued = shown.filter((task) => task.status === 'queued').length;
  const parts = [inProgress && `${inProgress} running`, queued && `${queued} queued`].filter(Boolean);

  return parts.length ? parts.join(' · ') : 'All done';
};

const RecentTasks: React.FC<{ tasks: Task[] }> = ({ tasks }) => {
  const [open, setOpen] = useState(false);

  if (!tasks.length) return null;

  return (
    <>
      <button type="button" className="active-tasks-dock__recent-toggle" aria-expanded={open}
        onClick={() => setOpen((value) => !value)}>
        <i className="fa-solid fa-chevron-down" aria-hidden="true" />
        Recently done · {tasks.length}
      </button>
      {open && <TaskChecklistRows items={tasks.map((task) => toItem(task))} completionOnly onViewTask={viewTask} />}
    </>
  );
};

interface DockView { shown: Task[]; recent: Task[]; live: boolean }

const rowKey = (task: Task): string => [task.id, task.title, task.status, task.execution_step ?? ''].join('\u0000');
const viewKey = (view: DockView): string => (
  JSON.stringify([view.live, view.shown.map(rowKey), view.recent.map(rowKey)])
);

/** The last thing the dock showed, kept so a departing sheet still has rows to slide away with. */
const useLastView = (view: DockView | null): DockView | null => {
  const [last, setLast] = useState(view);

  if (view && (!last || viewKey(view) !== viewKey(last))) setLast(view);

  return view ?? last;
};

const DockHeader: React.FC<{ view: DockView; open: boolean; onToggle: () => void }> = ({ view, open, onToggle }) => (
  <div className="active-tasks-dock__header">
    <button type="button" className="active-tasks-dock__toggle" aria-expanded={open} onClick={onToggle}>
      <span className={`active-tasks-dock__mark${view.live ? ' activity-ring' : ' active-tasks-dock__mark--done'}`}
        aria-hidden="true" />
      <span className="active-tasks-dock__title">{view.live ? 'Running tasks' : 'Tasks finished'}</span>
      <span className="active-tasks-dock__summary phase-task-picker__totals" aria-live="polite">
        {summary(view.shown, view.live)}
      </span>
      <i className="fa-solid fa-chevron-down active-tasks-dock__chevron" aria-hidden="true" />
    </button>
    <NotifyMeButton />
  </div>
);

const dockClassName = (view: DockView, leaving: boolean): string => (
  `active-tasks-dock phase-task-picker${view.live ? '' : ' active-tasks-dock--settling'}`
  + (leaving ? ' active-tasks-dock--leaving' : '')
);

/**
 * The tasks of the current attempt, docked above the composer while any of
 * them runs. A task that finishes stays for ten seconds marked done, then
 * moves under the "Recently done" arrow. The transcript's own checklist is
 * untouched: this is the live view, that is the record.
 *
 * The sheet slides: it grows up out of the composer when it arrives, its
 * height follows the rows as tasks come and go, and it sinks back down
 * before it unmounts.
 */
// eslint-disable-next-line max-statements
export const ActiveTasksDock: React.FC<Props> = ({ projectId, hidden, running = false }) => {
  const { tasks } = usePlanningTasks(projectId, Boolean(projectId) && !hidden);
  const follow = useCompletionFollow(running ? tasks.filter(isActivePlanningTask).map((task) => task.id) : []);
  // Open by default so the running rows are in view; only the recently-done list starts folded.
  const [open, setOpen] = useState(true);
  const { shown, recent } = dockTasks(tasks, follow, running);
  const present = Boolean(projectId) && !hidden && shown.length > 0;
  const { mounted, leaving } = useDeparture(present);
  const view = useLastView(present ? { shown, recent, live: running && shown.some(isActivePlanningTask) } : null);
  const { sheetRef, height } = useSheetHeight(mounted, leaving);

  if (!mounted || !view) return null;

  return (
    <section className={dockClassName(view, leaving)} aria-label="Running tasks"
      style={height === null ? undefined : { height }}>
      <div className="active-tasks-dock__sheet" ref={sheetRef}>
        <DockHeader view={view} open={open} onToggle={() => setOpen((value) => !value)} />
        {open && (
          <div className="active-tasks-dock__body">
            <TaskChecklistRows items={view.shown.map((task) => toItem(task, view.live))} completionOnly
              onViewTask={viewTask} />
            <RecentTasks tasks={view.recent} />
          </div>
        )}
      </div>
    </section>
  );
};
